import type { ReactNode } from 'react';
import { Input } from '@/shared/ui/input';
import { Select } from '@/shared/ui/select';
import type { SelectOption } from '@/shared/ui/select';
import { Textarea } from '@/shared/ui/textarea';
import type {
  CategoryDraft,
  CategoryDraftPatch,
} from '@/modules/catalog/pages/setup-page/types';
import { OutcomeLine } from '@/modules/catalog/pages/setup-page/parts/outcome-line';
import { DraftAccept } from '@/modules/catalog/pages/setup-page/parts/draft-accept';

/**
 * ONE proposed category, editable.
 *
 * Every field the merchant can change is a real control, and the checkbox says
 * whether this row is saved. The `unsure` marker is always visible when the
 * proposal said it was unsure: a quiet marker nobody notices is not a marker.
 */
export type CategoryRowProps = {
  readonly draft: CategoryDraft;
  readonly parentOptions: SelectOption[];
  readonly onChange: (id: string, patch: CategoryDraftPatch) => void;
};

export function CategoryRow({
  draft,
  parentOptions,
  onChange,
}: CategoryRowProps): ReactNode {
  return (
    <article className="space-y-3 rounded-lg border border-border bg-surface-raised p-3">
      <DraftAccept
        label={`Accept ${draft.name}`}
        accepted={draft.accepted}
        uncertain={draft.uncertain}
        onToggle={() => onChange(draft.id, { accepted: !draft.accepted })}
      />

      <div className="grid gap-3 md:grid-cols-2">
        <Input
          label="Name"
          value={draft.name}
          onChange={(event) => onChange(draft.id, { name: event.target.value })}
        />
        <Input
          label="Slug"
          hint="Lower kebab-case. The catalogue API refuses anything else."
          value={draft.slug}
          onChange={(event) => onChange(draft.id, { slug: event.target.value })}
        />
      </div>

      <Textarea
        label="Description"
        value={draft.description}
        onChange={(event) => onChange(draft.id, { description: event.target.value })}
      />

      <Select
        label="Parent category"
        options={parentOptions}
        value={draft.parentCategoryId}
        onChange={(event) => onChange(draft.id, { parentCategoryId: event.target.value })}
      />

      {draft.parentProposedName === '' ? null : (
        <p className="text-sm text-content-muted">
          The proposal places this under “{draft.parentProposedName}”. Save that category first,
          then choose it as the parent here.
        </p>
      )}

      <OutcomeLine outcome={draft.outcome} />
    </article>
  );
}
